import { useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { ErrorList } from '@/shared/ui/ErrorList';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { RadioGroupField } from '@/shared/ui/RadioGroupField';
import type { SelectOption } from '@/shared/ui/selectTypes';
import { TextField } from '@/shared/ui/TextField';
import {
  LOYALTY_PROMO_TYPE,
  savePromocode,
  type PromocodeValues,
} from './promoSectionsApi';

type Props = {
  promoId: number;
  /** Set when editing an existing promocode. */
  id?: number;
  initial: PromocodeValues;
  types: SelectOption[];
  discountLevels: SelectOption[];
  onClose: () => void;
};

export const PromocodeModal = ({ promoId, id, initial, types, discountLevels, onClose }: Props) => {
  const qc = useQueryClient();
  const { values, errors, setField, save, isSaving } = useEntityForm(
    initial,
    (v: PromocodeValues) => savePromocode(promoId, id, v),
    () => {
      void qc.invalidateQueries({ queryKey: ['promocodes', promoId] });
      onClose();
    },
  );
  const isLoyalty = values.typeId === LOYALTY_PROMO_TYPE;

  return (
    <ModalDialog
      title={id ? `Редактирование промокода #${id}` : 'Создать промокод'}
      onClose={onClose}
      onSave={save}
      isSaving={isSaving}
    >
      <TextField label="Code *" placeholder="Code" value={values.code} errors={errors.code} onChange={(v) => setField('code', v)} />
      {!isLoyalty && (
        <TextField
          label="Сумма промокода *"
          type="number"
          placeholder="Сумма промокода"
          value={values.amount}
          errors={errors.amount}
          onChange={(v) => setField('amount', v)}
        />
      )}
      <TextField
        label="Количество *"
        type="number"
        placeholder="Количество"
        value={values.count}
        errors={errors.count}
        onChange={(v) => setField('count', v)}
      />
      <RadioGroupField
        label="Тип промокода *"
        name="promocode-type"
        options={types}
        value={values.typeId}
        errors={errors.type_id}
        onChange={(v) => setField('typeId', v, 'type_id')}
      />
      {isLoyalty && (
        <>
          <TextField
            label="Сроки (дни) *"
            type="number"
            placeholder="Количество дней"
            value={values.loyaltyDays}
            errors={errors.loyalty_days}
            onChange={(v) => setField('loyaltyDays', v, 'loyalty_days')}
          />
          <FormField label="Тип *" errors={errors.discount_level_id}>
            <FilterSelect
              options={discountLevels}
              value={values.discountLevelId}
              placeholder="Выберите уровень лояльности..."
              onChange={(v) => setField('discountLevelId', v, 'discount_level_id')}
            />
          </FormField>
        </>
      )}
      <TextField
        label="Дата истечения *"
        type="datetime-local"
        value={values.expiredTime}
        errors={errors.expired_time}
        onChange={(v) => setField('expiredTime', v, 'expired_time')}
      />
      <TextField
        label="Дата сгорания *"
        type="datetime-local"
        value={values.burnTime}
        errors={errors.burn_time}
        onChange={(v) => setField('burnTime', v, 'burn_time')}
      />
      <ErrorList errors={{ common: errors.common ?? [], server: errors.server ?? [] }} />
    </ModalDialog>
  );
};
